import React from 'react';
import { motion } from 'framer-motion';
import { Scale, FileText, Shield } from 'lucide-react';

const Terms = () => {
  return (
    <div className="terms-page">
      {/* Hero Section */}
      <section className="terms-hero">
        <div className="terms-hero-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="terms-hero-content"
          >
            <div className="terms-hero-badge">
              <Scale className="terms-hero-badge-icon" />
              <span>Legal Terms</span>
            </div>
            <h1 className="terms-hero-title">
              Terms of
              <span className="terms-hero-title-gradient"> Service</span>
            </h1>
            <p className="terms-hero-subtitle">
              Please read these terms carefully as they govern your use of ChartLine Brokerage services and platform.
            </p>
            <div className="terms-hero-date">
              Last updated: January 15, 2024
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="terms-content-section">
        <div className="terms-content-container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="terms-content"
          >
            
            <div className="terms-section">
              <h2 className="terms-section-title">1. Acceptance of Terms</h2>
              <p className="terms-section-text">
                By accessing and using ChartLine Brokerage services, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">2. Trading Services</h2>
              <p className="terms-section-text">
                ChartLine Brokerage provides online trading services for stocks, cryptocurrencies, forex, and other financial instruments. Our services are subject to market conditions and regulatory requirements.
              </p>
              <ul className="terms-list">
                <li>Trading involves substantial risk and may not be suitable for all investors</li>
                <li>Past performance does not guarantee future results</li>
                <li>You are responsible for all trading decisions made on your account</li>
                <li>We reserve the right to restrict or suspend trading in certain circumstances</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">3. Account Responsibilities</h2>
              <p className="terms-section-text">
                You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
              </p>
              <ul className="terms-list">
                <li>Provide accurate and complete information during registration</li>
                <li>Maintain the security of your login credentials</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">4. Fees and Charges</h2>
              <p className="terms-section-text">
                Trading fees, subscription costs, and other charges are clearly outlined in our pricing structure. Fees may change with prior notice as required by law.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">5. Risk Disclosure</h2>
              <p className="terms-section-text">
                Trading financial instruments involves substantial risk of loss and is not suitable for all investors. The high degree of leverage can work against you as well as for you.
              </p>
              <div className="terms-warning">
                <Shield className="terms-warning-icon" />
                <div>
                  <strong>Important Risk Warning:</strong> You should carefully consider whether trading is suitable for you in light of your financial circumstances, knowledge, and experience.
                </div>
              </div>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">6. Limitation of Liability</h2>
              <p className="terms-section-text">
                ChartLine Brokerage shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">7. Data Protection</h2>
              <p className="terms-section-text">
                We are committed to protecting your personal information. Please refer to our Privacy Policy for detailed information about how we collect, use, and protect your data.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">8. Modifications to Terms</h2>
              <p className="terms-section-text">
                We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services constitutes acceptance of the modified terms.
              </p>
            </div>

            <div className="terms-section">
              <h2 className="terms-section-title">9. Contact Information</h2>
              <p className="terms-section-text">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="terms-contact">
                <p><strong>Email:</strong> legal@chartlinebrokerage.com</p>
                <p><strong>Phone:</strong> +1 239 791 0799</p>
                <p><strong>Address:</strong> 2268 S Tongass Hwy, Ketchikan, Alaska(AK), 99901</p>
              </div>
            </div>

          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="terms-cta-section">
        <div className="terms-cta-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="terms-cta-content"
          >
            <FileText className="terms-cta-icon" />
            <h2 className="terms-cta-title">
              Have Questions About Our Terms?
            </h2>
            <p className="terms-cta-subtitle">
              Our legal team is here to help clarify any questions you may have.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="terms-cta-button"
            >
              Contact Legal Team
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Terms; 